import { useCallback, useState } from 'react';
import Select from 'react-select-2';
import type { VoidFunctionComponent } from 'react';
import type { ValueType } from 'react-select-2/lib/types';

import { confirmationMessage } from '../../../shared/InternalTools/cache';
import { useDialogContext } from '../../contexts/DialogContext';
import useAsyncStatus from '../../hooks/useAsyncStatus';
import { remove } from '../../redux/api';
import { selectOptionFactory } from '../../utils';
import Accordion from '../generic/Accordion';
import { InlineLoaderButton } from '../generic/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import type { FailedFetchResponse } from '../../helpers/errors';

type InvalidateCacheComponentProps = {
  cacheKinds: string[];
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

const InvalidateCacheComponent: VoidFunctionComponent<InvalidateCacheComponentProps> = ({
  cacheKinds,
  isOpen,
  onToggle,
}) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const { onConfirm } = useDialogContext();
  const [cacheKind, setCacheKind] = useState<string>(cacheKinds[0] || 'NO-CANDIDATE-CACHE-KIND-FOUND!');
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  const removeCache = useCallback(async () => {
    onPending();
    try {
      await remove(`/api/internal-tools/caches/${cacheKind}`);
      onSuccess();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [cacheKind, onPending, onSuccess, onError]);

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="invalidate-cache" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>Invalidate Cache</Accordion.Target>
          <Accordion.Body>
            <Flex justify="left" gap={10}>
              <div className="width-quarter">
                <Select
                  value={selectOptionFactory(cacheKind)}
                  options={cacheKinds.map((f: string) => selectOptionFactory(f))}
                  onChange={(option: ValueType<{ value: string; label: string }>) => {
                    setCacheKind((option as { value: string }).value);
                  }}
                />
              </div>

              <InlineLoaderButton
                onClick={() => {
                  onConfirm({
                    title: 'Are you sure?',
                    body: confirmationMessage(cacheKind),
                    onConfirm: removeCache,
                  });
                }}
                isLoading={status.loading}
                isDisabled={status.loading}
              >
                Submit
              </InlineLoaderButton>
            </Flex>
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default InvalidateCacheComponent;
